import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Search, Loader2, Image, Camera, X, Check, ChevronDown, ExternalLink,
  RectangleHorizontal, RectangleVertical, Square, Palette
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { apiClient } from '@/lib/api';
import type { LandingSection } from '@easyflow/shared';

interface StockImage {
  id: number;
  url: string;
  urlOriginal: string;
  urlLandscape: string;
  thumb: string;
  tiny: string;
  alt: string;
  photographer: string;
  photographerUrl: string;
  avgColor: string;
  width: number;
  height: number;
}

interface ImageTarget {
  label: string;
  sectionId: string;
  sectionType: string;
  field: string;
  index?: number;
  currentUrl?: string;
}

interface StockImageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sections: LandingSection[];
  onImageSelect: (imageUrl: string, target: ImageTarget | null) => void;
  initialTarget?: string;
}

const ORIENTATION_OPTIONS = [
  { value: 'all', label: 'Tutte', icon: Image },
  { value: 'landscape', label: 'Orizzontale', icon: RectangleHorizontal },
  { value: 'portrait', label: 'Verticale', icon: RectangleVertical },
  { value: 'square', label: 'Quadrato', icon: Square },
];

const COLOR_OPTIONS = [
  { value: '', label: 'Tutti i colori' },
  { value: 'red', label: 'Rosso', hex: '#ef4444' },
  { value: 'orange', label: 'Arancione', hex: '#f97316' },
  { value: 'yellow', label: 'Giallo', hex: '#eab308' },
  { value: 'green', label: 'Verde', hex: '#22c55e' },
  { value: 'turquoise', label: 'Turchese', hex: '#06b6d4' },
  { value: 'blue', label: 'Blu', hex: '#3b82f6' },
  { value: 'violet', label: 'Viola', hex: '#8b5cf6' },
  { value: 'pink', label: 'Rosa', hex: '#ec4899' },
  { value: 'brown', label: 'Marrone', hex: '#a16207' },
  { value: 'black', label: 'Nero', hex: '#171717' },
  { value: 'gray', label: 'Grigio', hex: '#6b7280' },
  { value: 'white', label: 'Bianco', hex: '#f5f5f5' },
];

// Il modello sezione è dinamico: `props` è un blob jsonb libero. Leggiamo qui
// solo le chiavi immagine note, coercendo i valori (unknown) a stringa dove la
// UI si aspetta un URL/label.
function extractImageTargets(sections: LandingSection[]): ImageTarget[] {
  const targets: ImageTarget[] = [];
  const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');
  for (const section of sections) {
    const props = (section.props ?? {}) as Record<string, unknown>;
    const sectionId = section.id ?? '';
    const sType = section.type ?? '';
    const sLabel = asStr(props.title) || asStr(props.heading) || sectionId;
    if (props.backgroundImage !== undefined) {
      targets.push({
        label: `${sLabel} → Sfondo`,
        sectionId,
        sectionType: sType,
        field: 'backgroundImage',
        currentUrl: asStr(props.backgroundImage)
      });
    }
    if (props.image !== undefined) {
      targets.push({
        label: `${sLabel} → Immagine`,
        sectionId,
        sectionType: sType,
        field: 'image',
        currentUrl: asStr(props.image)
      });
    }
    if (Array.isArray(props.images)) {
      (props.images as Array<{ url?: unknown }>).forEach((img, idx) => {
        targets.push({
          label: `${sLabel} → Immagine ${idx + 1}`,
          sectionId,
          sectionType: sType,
          field: 'images',
          index: idx,
          currentUrl: asStr(img?.url)
        });
      });
    }
    if (Array.isArray(props.items)) {
      (props.items as Array<{ image?: unknown; url?: unknown; title?: unknown }>).forEach((item, idx) => {
        const itemTitle = asStr(item?.title);
        if (item?.image !== undefined) {
          targets.push({
            label: `${sLabel} → ${itemTitle || `Elemento ${idx + 1}`} (immagine)`,
            sectionId,
            sectionType: sType,
            field: 'items_image',
            index: idx,
            currentUrl: asStr(item.image)
          });
        }
        const itemUrl = asStr(item?.url);
        if (itemUrl !== '' && !itemUrl.startsWith('#') && !itemUrl.startsWith('http') && !itemUrl.startsWith('tel:') && !itemUrl.startsWith('mailto:')) {
          targets.push({
            label: `${sLabel} → ${itemTitle || `Elemento ${idx + 1}`} (logo/icona)`,
            sectionId,
            sectionType: sType,
            field: 'items_url',
            index: idx,
            currentUrl: itemUrl
          });
        }
      });
    }
    if (props.avatar !== undefined) {
      targets.push({
        label: `${sLabel} → Avatar`,
        sectionId,
        sectionType: sType,
        field: 'avatar',
        currentUrl: asStr(props.avatar)
      });
    }
  }
  return targets;
}

export default function StockImageModal({ open, onOpenChange, sections, onImageSelect, initialTarget }: StockImageModalProps) {
  const [query, setQuery] = useState('');
  const [images, setImages] = useState<StockImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [orientation, setOrientation] = useState('all');
  const [color, setColor] = useState('');
  const [selectedImage, setSelectedImage] = useState<StockImage | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<string>(initialTarget || '__new__');
  const [previewImage, setPreviewImage] = useState<StockImage | null>(null);

  useEffect(() => {
    if (open && initialTarget) {
      setSelectedTarget(initialTarget);
    }
  }, [open, initialTarget]);
  const [error, setError] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const targets = extractImageTargets(sections);

  const searchImages = useCallback(async (searchQuery: string, searchPage: number, append: boolean = false) => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {
        query: searchQuery.trim(),
        page: String(searchPage),
        per_page: '24'
      };
      if (orientation !== 'all') params.orientation = orientation;
      if (color) params.color = color;

      const qs = new URLSearchParams(params).toString();
      const res = await apiClient.get<{
        images: StockImage[];
        total: number;
        page: number;
        hasMore: boolean;
      }>(`/landing-pages/search-images?${qs}`);

      if (res.data) {
        setImages(prev => append ? [...prev, ...res.data!.images!] : res.data!.images!);
        setTotal(res.data.total);
        setHasMore(res.data.hasMore);
        setPage(searchPage);
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || '';
      if (msg.includes('Pexels API key not configured')) {
        setError('API Pexels non configurata. Aggiungi la chiave PEXELS_API_KEY nelle variabili d\'ambiente del server.');
      } else {
        setError('Errore nella ricerca immagini. Riprova.');
      }
    } finally {
      setLoading(false);
    }
  }, [orientation, color]);

  const handleSearch = useCallback(() => {
    if (query.trim()) {
      setImages([]);
      searchImages(query, 1);
    }
  }, [query, searchImages]);

  useEffect(() => {
    if (!open) {
      setSelectedImage(null);
      setPreviewImage(null);
      return;
    }
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    if (!query.trim() || !open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setImages([]);
      searchImages(query, 1);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [orientation, color, open, query, searchImages]);

  const handleConfirm = () => {
    if (!selectedImage) return;
    const target = selectedTarget === '__new__' ? null : targets.find(t => `${t.sectionId}::${t.field}::${t.index ?? ''}` === selectedTarget) || null;
    onImageSelect(selectedImage.url, target);
    onOpenChange(false);
    setSelectedImage(null);
    setPreviewImage(null);
    setQuery('');
    setImages([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Image className="h-5 w-5 text-sky-500" />
            Immagini Stock
            {total > 0 && <Badge variant="secondary" className="text-xs font-normal">{total.toLocaleString()} risultati</Badge>}
          </DialogTitle>

          <div className="flex items-center gap-2 mt-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                ref={searchInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Cerca immagini... (es. ufficio, ristorante, tecnologia)"
                className="pl-9 h-9 text-sm"
              />
            </div>
            <Button size="sm" onClick={handleSearch} disabled={loading || !query.trim()} className="h-9 px-4">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 gap-0.5">
              {ORIENTATION_OPTIONS.map(opt => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setOrientation(opt.value)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                      orientation === opt.value
                        ? 'bg-white dark:bg-gray-700 text-sky-600 shadow-sm font-medium'
                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                    title={opt.label}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                );
              })}
            </div>

            <Select value={color || 'all_colors'} onValueChange={(v) => setColor(v === 'all_colors' ? '' : v)}>
              <SelectTrigger className="h-7 w-auto min-w-[130px] text-xs gap-1">
                <Palette className="h-3.5 w-3.5 text-gray-400" />
                <SelectValue placeholder="Colore" />
              </SelectTrigger>
              <SelectContent>
                {COLOR_OPTIONS.map(c => (
                  <SelectItem key={c.value} value={c.value || 'all_colors'} className="text-xs">
                    <div className="flex items-center gap-2">
                      {c.hex && <span className="w-3 h-3 rounded-full border border-gray-200" style={{ backgroundColor: c.hex }} />}
                      {c.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-0">
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm mb-3">
              <X className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {images.length === 0 && !loading && !error && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Camera className="h-12 w-12 mb-3 opacity-50" />
              <p className="text-sm font-medium">Cerca immagini stock gratuite</p>
              <p className="text-xs mt-1">Powered by Pexels — foto professionali royalty-free</p>
            </div>
          )}

          {loading && images.length === 0 && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
            </div>
          )}

          {images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {images.map((img) => (
                <button
                  key={img.id}
                  onClick={() => setSelectedImage(selectedImage?.id === img.id ? null : img)}
                  onDoubleClick={() => { setSelectedImage(img); setPreviewImage(img); }}
                  className={`group relative aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage?.id === img.id
                      ? 'border-sky-500 ring-2 ring-sky-200 dark:ring-sky-800 scale-[0.97]'
                      : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <img
                    src={img.thumb}
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    style={{ backgroundColor: img.avgColor }}
                  />
                  {selectedImage?.id === img.id && (
                    <div className="absolute inset-0 bg-sky-500/20 flex items-center justify-center">
                      <div className="bg-sky-500 text-white rounded-full p-1.5">
                        <Check className="h-4 w-4" />
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-[10px] text-white truncate">{img.photographer}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {hasMore && !loading && images.length > 0 && (
            <div className="flex justify-center mt-4">
              <Button variant="outline" size="sm" onClick={() => searchImages(query, page + 1, true)}>
                <ChevronDown className="h-4 w-4 mr-1" />
                Carica altre immagini
              </Button>
            </div>
          )}

          {loading && images.length > 0 && (
            <div className="flex justify-center mt-4">
              <Loader2 className="h-5 w-5 animate-spin text-sky-500" />
            </div>
          )}
        </div>

        {selectedImage && (
          <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-5 py-3 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-3">
              <img
                src={selectedImage.thumb}
                alt={selectedImage.alt}
                className="w-16 h-12 object-cover rounded-md border border-gray-200 dark:border-gray-600"
                style={{ backgroundColor: selectedImage.avgColor }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 truncate">
                  📸 {selectedImage.photographer} · {selectedImage.width}×{selectedImage.height}
                </p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs text-gray-500 shrink-0">Inserisci in:</span>
                  <Select value={selectedTarget} onValueChange={setSelectedTarget}>
                    <SelectTrigger className="h-7 text-xs flex-1 min-w-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__new__" className="text-xs">📋 Copia URL (usa nel prompt)</SelectItem>
                      {targets.map(t => {
                        const key = `${t.sectionId}::${t.field}::${t.index ?? ''}`;
                        return (
                          <SelectItem key={key} value={key} className="text-xs">
                            {t.currentUrl ? '🔄' : '➕'} {t.label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button size="sm" onClick={handleConfirm} className="h-8 px-4 bg-sky-500 hover:bg-sky-600">
                <Check className="h-3.5 w-3.5 mr-1" />
                Inserisci
              </Button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 flex items-center gap-1">
              <ExternalLink className="h-3 w-3" />
              Foto di Pexels — gratuita per uso commerciale
            </p>
          </div>
        )}

        {previewImage && (
          <div
            className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center cursor-pointer"
            onClick={() => setPreviewImage(null)}
          >
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={previewImage.url}
              alt={previewImage.alt}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-lg text-sm">
              📸 {previewImage.photographer} · {previewImage.width}×{previewImage.height}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
